import { ClientResponseError } from 'pocketbase'

/** Точные совпадения сообщений PocketBase / Supabase → русский. */
const EXACT: Record<string, string> = {
  'Failed to delete record. Make sure that the record is not part of a required relation reference.':
    'Не удалось удалить запись: на неё ссылаются другие данные (график производства, отгрузки и т.п.). Сначала удалите или отвяжите связанные записи.',
  'Failed to delete record.':
    'Не удалось удалить запись.',
  'Failed to create record.':
    'Не удалось создать запись.',
  'Failed to update record.':
    'Не удалось обновить запись.',
  'Failed to authenticate.':
    'Не удалось выполнить вход.',
  "The requested resource wasn't found.":
    'Запись не найдена или удалена.',
  'Invalid login credentials.':
    'Неверный email или пароль.',
  'Invalid login credentials':
    'Неверный email или пароль.',
  'Something went wrong while processing your request.':
    'Ошибка на сервере. Попробуйте позже или обратитесь к администратору.',
  'Only admins can perform this action.':
    'Недостаточно прав: действие доступно только администратору.',
  'Only superusers can perform this action.':
    'Недостаточно прав: действие доступно только суперпользователю.',
  'An error occurred while validating the submitted data.':
    'Ошибка проверки данных. Проверьте заполнение полей.',
  'Missing or invalid auth collection context.':
    'Ошибка конфигурации входа. Обратитесь к администратору.',
  'Missing or invalid collection context.':
    'Ошибка конфигурации коллекции. Обратитесь к администратору.',
  'The request was autocancelled. You can find more info in https://github.com/pocketbase/js-sdk#auto-cancellation.':
    'Запрос отменён (открыта другая страница). Обновите список.',
  'File upload failed.':
    'Не удалось загрузить файл.',
  'Cannot be blank.':
    'Поле обязательно для заполнения.',
  'Invalid value.':
    'Некорректное значение.',
  'Invalid value format.':
    'Некорректный формат значения.',
  'Must be unique.':
    'Такое значение уже существует.',
  'Invalid Refresh Token: Refresh Token Not Found':
    'Сессия истекла. Войдите снова.',
  'Email not confirmed':
    'Email не подтверждён.',
  'User already registered':
    'Пользователь с таким email уже зарегистрирован.',
  'Network request failed':
    'Нет связи с сервером. Проверьте интернет и адрес API.',
  'Failed to fetch':
    'Нет связи с сервером. Проверьте интернет и адрес API.',
}

/** Шаблоны: [regex, русский текст или функция подстановки]. */
const PATTERNS: Array<[RegExp, string | ((m: RegExpMatchArray) => string)]> = [
  [/required relation reference/i, 'на запись ссылаются другие данные — сначала удалите или отвяжите связанные записи'],
  [/invalid login/i, 'Неверный email или пароль'],
  [/wasn't found/i, 'Запись не найдена или удалена'],
  [/wasn\'t found/i, 'Запись не найдена или удалена'],
  [/^Failed to delete record\.?\s*(.*)$/i, (m) => {
    const rest = m[1]?.trim()
    return rest
      ? `Не удалось удалить запись: ${translateErrorMessage(rest)}`
      : 'Не удалось удалить запись.'
  }],
  [/^Failed to create record\.?\s*(.*)$/i, (m) => {
    const rest = m[1]?.trim()
    return rest
      ? `Не удалось создать запись: ${translateErrorMessage(rest)}`
      : 'Не удалось создать запись.'
  }],
  [/^Failed to update record\.?\s*(.*)$/i, (m) => {
    const rest = m[1]?.trim()
    return rest
      ? `Не удалось обновить запись: ${translateErrorMessage(rest)}`
      : 'Не удалось обновить запись.'
  }],
  [/Must be no more than (\d+) character/i, 'Не более $1 символов'],
  [/Must be at least (\d+) character/i, 'Не менее $1 символов'],
  [/Must be less than or equal to ([\d.]+)/i, 'Значение должно быть не больше $1'],
  [/Must be greater than or equal to ([\d.]+)/i, 'Значение должно быть не меньше $1'],
  [/Must be less than ([\d.]+)/i, 'Значение должно быть меньше $1'],
  [/Must be greater than ([\d.]+)/i, 'Значение должно быть больше $1'],
  [/insufficient permissions/i, 'Недостаточно прав для этого действия'],
  [/forbidden/i, 'Доступ запрещён'],
  [/unauthorized/i, 'Требуется вход в систему'],
  [/rate limit/i, 'Слишком много запросов. Подождите и попробуйте снова'],
  [/timeout/i, 'Превышено время ожидания ответа сервера'],
]

function looksRussian(text: string): boolean {
  return /[а-яА-ЯёЁ]/.test(text)
}

/** Переводит известные англоязычные сообщения API на русский. */
export function translateErrorMessage(message: string): string {
  const trimmed = message.trim()
  if (!trimmed) return trimmed
  if (looksRussian(trimmed)) return trimmed

  const exact = EXACT[trimmed]
  if (exact) return exact

  for (const [re, repl] of PATTERNS) {
    const m = trimmed.match(re)
    if (m) {
      return typeof repl === 'function' ? repl(m) : trimmed.replace(re, repl)
    }
  }

  return trimmed
}

function formatPbFieldData(data: Record<string, unknown>): string {
  const parts: string[] = []
  for (const [key, val] of Object.entries(data)) {
    if (val == null) continue
    if (typeof val === 'object' && !Array.isArray(val) && 'message' in val) {
      const m = (val as { message?: string }).message
      if (m) parts.push(`${key}: ${translateErrorMessage(m)}`)
    } else if (Array.isArray(val)) {
      const msgs = val
        .filter((x): x is string => typeof x === 'string')
        .map((x) => translateErrorMessage(x))
      if (msgs.length) parts.push(`${key}: ${msgs.join(', ')}`)
    } else if (typeof val === 'string') {
      parts.push(`${key}: ${translateErrorMessage(val)}`)
    }
  }
  return parts.join('; ')
}

/** Текст ошибки PocketBase / API с переводом и деталями валидации по полям. */
export function pocketbaseErrorMessage(e: unknown, fallback = 'Ошибка запроса'): string {
  if (e instanceof ClientResponseError) {
    const r = e.response as { message?: string; data?: Record<string, unknown> }
    const raw = (typeof r?.message === 'string' && r.message) || e.message || ''
    const base = raw ? translateErrorMessage(raw) : fallback
    const fields = r?.data && formatPbFieldData(r.data)
    if (fields) return `${base} — ${fields}`
    if (e.status === 403 && base === raw) return 'Недостаточно прав для этого действия.'
    if (e.status === 404 && (!raw || base === raw)) return 'Запись не найдена или удалена.'
    return base || fallback
  }
  if (e instanceof Error) {
    const msg = e.message.trim()
    return msg ? translateErrorMessage(msg) : fallback
  }
  if (typeof e === 'string' && e.trim()) return translateErrorMessage(e.trim())
  return fallback
}
